import { useQuery } from "@tanstack/react-query";
import { navigate } from "gatsby";
import React, {
  createContext,
  useCallback,
  useContext,
  useReducer,
} from "react";
import { checkoutClient } from "@/api/checkoutClient";
import { EndpointBuilder } from "@/util/endpoints";

const CheckoutContext = createContext();
export const useCheckoutContext = () => useContext(CheckoutContext);

const initialState = {
  cart: [],
  customer: {},
  billingAddress: {},
  shippingAddress: {},
  details: {},
  paymentOptions: {},
};

function checkoutReducer(state, action) {
  switch (action.type) {
    case "SET_ALL":
      return { ...state, ...action.payload };
    case "UPDATE":
      return {
        ...state,
        ...Object.entries(action.payload).reduce((acc, [key, value]) => {
          acc[key] = value ?? state[key];
          return acc;
        }, {}),
      };
    default:
      return state;
  }
}

export const CheckoutProvider = ({ uuid, children }) => {
  const persisted = (() => {
    try {
      const saved = JSON.parse(
        sessionStorage.getItem("paymentContextData") || "{}",
      );
      return saved && saved.checkoutUuid
        ? {
            cart: saved.cart || [],
            customer: saved.customer || {},
            billingAddress: saved.billingAddress || {},
            shippingAddress: saved.shippingAddress || {},
            details: saved.details || {},
            paymentOptions: saved.paymentOptions || {},
          }
        : {};
    } catch {
      return {};
    }
  })();

  const [state, dispatch] = useReducer(checkoutReducer, {
    ...initialState,
    ...persisted,
  });

  const { isLoading, error } = useQuery({
    queryKey: ["checkoutData", uuid],
    queryFn: async () => {
      if (typeof window === "undefined") return;
      if (typeof uuid !== "string") {
        throw new Error("UUID inválido");
      }
      try {
        const endpoint = EndpointBuilder.make()
          .v1()
          .path(`public/sessions/${uuid}/order`)
          .build();

        const response = await checkoutClient.get(endpoint, {
          headers: { "Content-Type": "application/json" },
        });
        return response.data;
      } catch (err) {
        navigate("/404");
      }
    },
    staleTime: 1000 * 60 * 5,
    enabled: !!uuid,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,

    onSuccess: (data) => {
      const attrs = data.attributes || {};
      const payload = {
        cart: attrs.items || [],
        customer: attrs.customer || {},
        billingAddress: attrs.billing_address || {},
        shippingAddress: attrs.shipping_address || {},
        details: {
          id: data?.id,
          uuid: data?.uuid ?? uuid,
          amount: attrs.amount,
          discount: attrs.discount ?? 0,
          items: (attrs.items || []).map(({ unit_price, quantity }) => ({
            unit_price,
            quantity,
          })),
        },
        paymentOptions: attrs.payment_provider?.payment_options || {},
      };

      dispatch({ type: "SET_ALL", payload });

      sessionStorage.setItem(
        "paymentContextData",
        JSON.stringify({
          checkoutUuid: uuid,
          ...payload,
        }),
      );
    },
  });

  const updateContext = useCallback(
    (updates) => {
      const mergedUpdates = {
        cart: updates?.cartData,
        customer: updates?.customerData,
        billingAddress: updates?.billingAddressData,
        shippingAddress: updates?.shippingAddressData,
        details: updates?.detailsData,
        paymentOptions: updates?.paymentData,
      };

      dispatch({ type: "UPDATE", payload: mergedUpdates });

      const current = JSON.parse(
        sessionStorage.getItem("paymentContextData") || "{}",
      );
      sessionStorage.setItem(
        "paymentContextData",
        JSON.stringify({
          ...current,
          ...mergedUpdates,
          checkoutUuid: uuid || current.checkoutUuid,
        }),
      );
    },
    [uuid],
  );

  return (
    <CheckoutContext.Provider
      value={{
        ...state,
        updateContext,
        isLoading,
        error,
      }}
    >
      {children}
    </CheckoutContext.Provider>
  );
};
